import { useState, useEffect } from 'react';
import Swal from 'sweetalert2';

interface ThemePickerModalProps {
  themes: any[];
  currentThemeId: number | string | null;
  isOpen: boolean;
  initialMode?: 'pick' | 'create';
  onClose: () => void;
  onPick: (theme: any) => void;
  onCreateNew: (name: string) => void;
  onDelete: (theme: any) => void;
}

export default function ThemePickerModal({
  themes,
  currentThemeId,
  isOpen,
  initialMode = 'pick',
  onClose,
  onPick,
  onCreateNew,
  onDelete
}: ThemePickerModalProps) {
  const [mode, setMode] = useState<'pick' | 'create'>(initialMode);
  const [newThemeName, setNewThemeName] = useState('');

  // Re-sync whenever the modal is (re)opened, so clicking "View Themes" after
  // "Create Theme" (or vice versa) always lands on the right tab instead of
  // remembering whatever tab was active last time.
  useEffect(() => {
    if (isOpen) setMode(initialMode);
  }, [isOpen, initialMode]);

  if (!isOpen) return null;

  const handleCreate = () => {
    if (!newThemeName.trim()) return;
    onCreateNew(newThemeName.trim());
    setNewThemeName('');
  };

  const handleDeleteClick = async (e: React.MouseEvent, theme: any) => {
    e.stopPropagation();
    const result = await Swal.fire({
      title: `Delete "${theme.name}"?`,
      text: 'This can\u2019t be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
      confirmButtonColor: '#DC2626',
      cancelButtonText: 'Cancel'
    });
    if (result.isConfirmed) {
      onDelete(theme);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <div
        className="bg-white dark:bg-gray-800 rounded-2xl max-w-lg w-full border border-gray-200 dark:border-gray-700 shadow-2xl overflow-hidden flex flex-col max-h-[80vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center bg-gray-50 dark:bg-gray-800/50">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white">Create / Pick New Theme</h3>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-lg">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/></svg>
          </button>
        </div>

        <div className="flex border-b border-gray-100 dark:border-gray-700 px-5 gap-2">
          <button
            onClick={() => setMode('pick')}
            className={`py-3 px-3 text-sm font-semibold border-b-2 transition-all ${mode === 'pick' ? 'border-brand-500 text-brand-600 dark:text-brand-400' : 'border-transparent text-gray-500 hover:text-gray-800 dark:text-gray-400'}`}
          >
            View Themes
          </button>
          <button
            onClick={() => setMode('create')}
            className={`py-3 px-3 text-sm font-semibold border-b-2 transition-all ${mode === 'create' ? 'border-brand-500 text-brand-600 dark:text-brand-400' : 'border-transparent text-gray-500 hover:text-gray-800 dark:text-gray-400'}`}
          >
            Create Blank
          </button>
        </div>

        <div className="p-5 overflow-y-auto flex-1">
          {mode === 'pick' ? (
            themes.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-8">No themes yet. Create one to get started.</p>
            ) : (
              <div className="space-y-2">
                {themes.map(theme => {
                  const isCurrent = currentThemeId === theme.id;
                  // NOTE: guarding on currentThemeId (whatever's loaded in the
                  // editor right now) as a stand-in for "is this the active
                  // theme" — matches the backend's DeleteColorAction guard,
                  // but confirm your getApiThemes() response actually has an
                  // `is_active` field per theme and swap to `theme.is_active`
                  // if so, since currentThemeId only reflects local UI state,
                  // not necessarily the true backend-active theme.
                  return (
                    <div
                      key={theme.id}
                      className={`w-full flex items-center justify-between p-3 rounded-xl border-2 transition-all ${
                        isCurrent
                          ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/20'
                          : 'border-gray-100 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                      }`}
                    >
                      <button onClick={() => onPick(theme)} className="flex-1 text-left min-w-0">
                        <p className="text-sm font-bold text-gray-900 dark:text-white truncate">{theme.name}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{theme.description || 'Shoplattice theme'}</p>
                      </button>
                      <div className="flex items-center gap-2 shrink-0 pl-3">
                        {isCurrent && (
                          <span className="text-[10px] font-black uppercase tracking-widest text-brand-600 dark:text-brand-400">Selected</span>
                        )}
                        <button
                          onClick={(e) => handleDeleteClick(e, theme)}
                          disabled={isCurrent}
                          title={isCurrent ? 'Can\u2019t delete the active theme' : 'Delete theme'}
                          className="p-1.5 text-gray-400 hover:text-red-500 disabled:opacity-30 disabled:hover:text-gray-400 disabled:cursor-not-allowed rounded-lg transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          ) : (
            <div className="space-y-3">
              <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Theme Name</label>
              <input
                type="text"
                value={newThemeName}
                onChange={(e) => setNewThemeName(e.target.value)}
                placeholder="e.g. Summer Theme"
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 focus:border-brand-500 focus:ring-1 focus:ring-brand-500 outline-none transition-all text-gray-800 dark:text-white"
              />
              <button
                onClick={handleCreate}
                disabled={!newThemeName.trim()}
                className="w-full px-5 py-2.5 text-sm font-bold bg-brand-500 hover:bg-brand-600 text-white rounded-xl shadow-lg shadow-brand-500/20 transition-all disabled:opacity-50"
              >
                Create Theme
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}